import React, { useState, useEffect } from "react";
import { Flex, Title } from "@mantine/core";
import { Box } from "@mantine/core";
import { CardPost } from "../../components/Card/CardPost";
import { CardPostBig } from "../../components/Card/CardPostBig";
import colorsCustom from "../../designTokens/colorsCustom";
import ViewportWrapper from "../../components/triggerWrapper/ViewportWrapper";
import { IconCalendarEvent } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { size } from "../../designTokens/other";
import { motion } from "framer-motion";
import { useIsMobile } from "../../hooks/useIsMobile";

// Custom Masonry component
const CustomMasonry = ({
	children,
	columnsCountBreakPoints,
	gutterBreakpoints,
}) => {
	const [columns, setColumns] = useState(2);
	const [gutter, setGutter] = useState(8);

	useEffect(() => {
		const updateLayout = () => {
			const width = window.innerWidth;
			let newColumns = 2;
			let newGutter = 8;

			for (const [breakpoint, count] of Object.entries(
				columnsCountBreakPoints
			)) {
				if (width >= breakpoint) {
					newColumns = count;
				}
			}

			for (const [breakpoint, gutterSize] of Object.entries(
				gutterBreakpoints
			)) {
				if (width >= breakpoint) {
					newGutter = gutterSize;
				}
			}

			setColumns(newColumns);
			setGutter(newGutter);
		};

		window.addEventListener("resize", updateLayout);
		updateLayout();

		return () => window.removeEventListener("resize", updateLayout);
	}, [columnsCountBreakPoints, gutterBreakpoints]);

	// Distribute children into columns
	const columnWrappers = Array.from({ length: columns }, () => []);

	children.forEach((child, index) => {
		const columnIndex = index % columns;
		columnWrappers[columnIndex].push(child);
	});

	return (
		<div
			style={{
				display: "flex",
				gap: `${gutter}px`,
			}}
		>
			{columnWrappers.map((column, index) => (
				<div
					key={index}
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						gap: `${gutter}px`,
					}}
				>
					{column.map((child, childIndex) => (
						<div key={childIndex}>
							{child}
						</div>
					))}
				</div>
			))}
		</div>
	);
};

const WeekList = ({ week }) => {
	const [sortedPosts, setSortedPosts] = useState([]);
	const isSM = useIsMobile('sm');

	useEffect(() => {
		const sorted = [...week.posts].sort((a, b) => a.timestamp - b.timestamp);
		setSortedPosts(sorted);
	}, [week.posts]);

	const remToPx = (remString) => {
		const remValue = parseFloat(remString.replace("rem", ""));
		return remValue * 16; // Assuming 1rem = 16px
	};

	// 카드 애니메이션 설정
	const cardVariants = {
		hidden: { opacity: 0, y: 50 },
		visible: (index) => ({
			opacity: 1,
			y: 0,
			transition: {
				delay: index * 0.02, // 딜레이를 더 크게 설정
				duration: 0.6,
				type: "spring",
				stiffness: 80,
				damping: 10
			}
		})
	};

	return (
		<CustomMasonry
			columnsCountBreakPoints={{
				0: 2,
				[remToPx(size.sm)]: 2,
				[remToPx(size.lg)]: 3,
				[remToPx(size.xl)]: 4,
			}}
			gutterBreakpoints={{
				0: 4,
				[remToPx(size.sm)]: 4,
				[remToPx(size.lg)]: 8,
				[remToPx(size.xl)]: 8,
			}}
		>
			{sortedPosts.map((thread, index) => {
				const isTopPost = thread.viewRank < 4 || thread.engagementRank < 4;
				
				return isTopPost ? (
					<motion.div
						key={thread.id}
						variants={cardVariants}
						initial="hidden"
						whileInView="visible" // 뷰포트에 나타났을 때 애니메이션 실행
						viewport={{ once: true, amount: 0.3 }} // 한 번만 트리거, 요소의 30%가 보일 때
						custom={index}
						whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
					>
						<CardPostBig
							rd={8}
							isMobile={isSM}
							thread={thread}
							date={thread.timestamp}
						/>
					</motion.div>
				) : (
					<motion.div
						key={thread.id}
						variants={cardVariants}
						initial="hidden"
						whileInView="visible" // 뷰포트에 나타났을 때 애니메이션 실행
						viewport={{ once: true, amount: 0.3 }} // 한 번만 트리거, 요소의 30%가 보일 때
						custom={index}
						whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
					>
						<CardPost
							rd={8}
							isMobile={isSM}
							thread={thread}
							date={thread.timestamp}
						/>
					</motion.div>
				);
			})}
		</CustomMasonry>
	);
};

export const WeeklyPostSectionCustomGrid = ({
	threadsList,
	profile,
	ref,
	setDisplayMonth,
	setDisplayYear,
}) => {
	const addOrdinalSuffix = (number) => {
		if (i18next.language === "ko") {
			return number;
		}

		if (number === "1") {
			return number + "st";
		}
		if (number === "2") {
			return number + "nd";
		}
		if (number === "3") {
			return number + "rd";
		}
		return number + "th";
	};
	const { t } = useTranslation();
	const isMobile = useIsMobile('xl');

	return (
		<Flex direction={"column"} gap={0} w={"100%"}>
			<ViewportWrapper
				onEnterViewport={() => {
					setDisplayMonth(threadsList.month);
					setDisplayYear(threadsList.year);
				}}
			>
				<Flex gap={4} ml={-2} align={"center"}>
					<IconCalendarEvent size={22} />
					<Title fw={500} order={isMobile ? 5 : 4} m={"8 0"} ref={ref}>
						{t("dashboard.dateMark", {
							month: threadsList.month,
							year: threadsList.year,
						})}
					</Title>
				</Flex>
			</ViewportWrapper>
			<Flex
				direction={"column"}
				pl={isMobile ? 8 : 16}
				pb={24}
				ml={isMobile ? 4 : 8}
				style={{
					position: "relative",
					borderLeft: `0.5px solid ${colorsCustom.ui.dark.subtle}`,
				}}
			>
				<Box
					style={{
						position: "absolute",
						bottom: 0,
						left: "-4px",
						width: 8,
						height: 8,
						borderLeft: `1px solid ${colorsCustom.ui.dark.subtle}`,
						borderBottom: `1px solid ${colorsCustom.ui.dark.subtle}`,
						transform: "rotate(-45deg)",
						backgroundColor: "transparent",
					}}
				/>
				{threadsList.week.map((week) => (
					<Flex key={week.title} direction={"column"} gap={0} pt={8}>
						<Title
							fw={500}
							order={isMobile ? 5 : 4}
							c={colorsCustom.ui.dark.moderate}
						>
							{t("dashboard.weekMark", {
								week: addOrdinalSuffix(week.title),
								numThreads: week.posts.length,
							})}
						</Title>
						<Box pt={16} pb={48}>
							<WeekList
								week={week}
							/>
						</Box>
					</Flex>
				))}
			</Flex>
		</Flex>
	);
};
